import { useEffect, useRef } from 'react';
import { refreshToken } from '../services/auth';
import { jwtDecode } from 'jwt-decode';

interface JWTPayload {
  _id: string;
  exp: number;
  iat: number;
}

/**
 * Hook to proactively refresh authentication token before it expires
 * Refreshes token 5 minutes before expiration to prevent 401 errors
 */
export const useTokenRefresh = () => {
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const scheduleTokenRefresh = () => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // Refresh 5 minutes (300000ms) before expiration
      // If token expires in less than 5 minutes, refresh immediately
      const refreshBuffer = 5 * 60 * 1000; // 5 minutes
      const timeUntilRefresh = Math.max(0, timeUntilExpiry - refreshBuffer);

      console.log('[TokenRefresh] Scheduling proactive refresh', {
        expiresAt: new Date(expiresAt).toISOString(),
        timeUntilExpiry: Math.round(timeUntilExpiry / 1000 / 60) + ' minutes',
        timeUntilRefresh: Math.round(timeUntilRefresh / 1000 / 60) + ' minutes',
      });

      // Schedule the refresh
      refreshTimerRef.current = setTimeout(async () => {
        if (isRefreshingRef.current) {
          console.log('[TokenRefresh] Refresh already in progress, skipping');
          return;
        }

        try {
          isRefreshingRef.current = true;
          console.log('[TokenRefresh] Proactively refreshing token');
          
          await refreshToken();
          
          console.log('[TokenRefresh] Token refreshed successfully');
          
          // Schedule the next refresh
          scheduleTokenRefresh();
        } catch (error) {
          console.error('[TokenRefresh] Failed to refresh token:', error);
          // Don't schedule another refresh if it failed
          // The user will be logged out on next API call
        } finally {
          isRefreshingRef.current = false;
        }
      }, timeUntilRefresh);

    } catch (error) {
      console.error('[TokenRefresh] Failed to decode token:', error);
    }
  };

  useEffect(() => {
    // Schedule initial refresh
    scheduleTokenRefresh();

    // Listen for storage events (token updates from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue) {
        console.log('[TokenRefresh] Token updated in another tab, rescheduling');
        scheduleTokenRefresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Listen for custom token update events (same tab)
    const handleTokenUpdate = () => {
      console.log('[TokenRefresh] Token updated, rescheduling');
      scheduleTokenRefresh();
    };

    window.addEventListener('token:updated', handleTokenUpdate);

    // Cleanup
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('token:updated', handleTokenUpdate);
    };
  }, []);

  return null;
};
